import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Video, Trash2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight';
import { videoTexts } from './videoTranslations';

const MAX_SECONDS = 180;

const readDuration = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const v = document.createElement('video');
  v.preload = 'metadata';
  v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration || 0); };
  v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
  v.src = url;
});

// Instagram-style video composer — 1 video (≤ 3 min) + description
export const VideoComposer = ({ onClose, onSuccess }) => {
  const { language } = useLanguage();
  const V = videoTexts(language);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [duration, setDuration] = useState(0);
  const [description, setDescription] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);
  const keyboardHeight = useKeyboardHeight();

  const pick = async (f) => {
    if (!f) return;
    if (!f.type.startsWith('video/') && !/\.(mp4|mov|m4v|webm)$/i.test(f.name)) { toast.error(V.errType); return; }
    const d = await readDuration(f);
    if (d && d > MAX_SECONDS + 1) { toast.error(V.errTooLong); return; }
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setDuration(d);
    setPreview(URL.createObjectURL(f));
  };

  const clear = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null); setPreview(null); setDuration(0);
  };

  const DIRECT_LIMIT = 48 * 1024 * 1024;

  const postChunk = async (upload_id, i, blob) => {
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const form = new FormData();
        form.append('upload_id', upload_id);
        form.append('index', String(i));
        form.append('chunk', blob, `part-${i}`);
        await axios.post('/api/videos/upload/chunk', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        return;
      } catch (e) {
        lastErr = e;
        if (e?.response?.status && e.response.status < 500 && e.response.status !== 408) throw e;
        await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
      }
    }
    throw lastErr;
  };

  const publish = async () => {
    if (!file) { toast.error(V.errNone); return; }
    setPublishing(true);
    setProgress(0);
    try {
      const meta = new FormData();
      meta.append('description', description);
      if (file.size <= DIRECT_LIMIT) {
        meta.append('video', file, file.name || 'video.mp4');
        await axios.post('/api/videos/create', meta, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (ev) => { if (ev.total) setProgress(Math.min(99, Math.round((ev.loaded / ev.total) * 100))); },
        });
      } else {
        const init = await axios.post('/api/videos/upload/init', { size: file.size, filename: file.name });
        const { upload_id, chunk_size } = init.data;
        const total = Math.ceil(file.size / chunk_size);
        for (let i = 0; i < total; i++) {
          await postChunk(upload_id, i, file.slice(i * chunk_size, (i + 1) * chunk_size));
          setProgress(Math.round(((i + 1) / total) * 100));
        }
        meta.append('upload_id', upload_id);
        await axios.post('/api/videos/create', meta, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setProgress(100);
      toast.success(V.published);
      onSuccess?.();
      onClose?.();
    } catch (e) {
      console.error('Video publish error:', e);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      toast.error(typeof detail === 'string' ? detail : `${V.errUpload}${status ? ` (HTTP ${status})` : e?.message ? ` (${e.message})` : ''}`);
    } finally {
      setPublishing(false);
    }
  };

  const mm = Math.floor(duration / 60); const ss = String(Math.floor(duration % 60)).padStart(2, '0');

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4"
      style={{ paddingBottom: keyboardHeight > 0 ? `${keyboardHeight + 16}px` : undefined, alignItems: keyboardHeight > 0 ? 'flex-start' : 'center', paddingTop: keyboardHeight > 0 ? '20px' : undefined }}
      onClick={publishing ? undefined : onClose}
      data-testid="video-composer-modal"
    >
      <div
        className="bg-gray-900 border border-white/10 rounded-3xl max-w-md w-full p-5 shadow-2xl overflow-y-auto"
        style={{ maxHeight: keyboardHeight > 0 ? `calc(100dvh - ${keyboardHeight}px - 40px)` : '90vh', transition: 'max-height 180ms ease-out' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="video-composer-card"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">{V.composerTitle}</h3>
          <button onClick={onClose} disabled={publishing} className="p-2 hover:bg-white/10 rounded-full disabled:opacity-40" data-testid="video-composer-close"><X className="w-5 h-5 text-white" /></button>
        </div>

        {preview ? (
          <div className={`relative rounded-2xl overflow-hidden bg-black mb-3 transition-all ${keyboardHeight > 0 ? 'h-24' : 'aspect-video'}`}>
            <video src={preview} className="w-full h-full object-contain" controls playsInline muted data-testid="video-composer-preview" />
            <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/70 rounded-full text-xs text-white font-mono">{mm}:{ss}</span>
            <button onClick={clear} disabled={publishing} className="absolute top-2 right-2 p-1.5 bg-black/70 rounded-full hover:bg-red-600 transition-colors" data-testid="video-composer-remove"><Trash2 className="w-3.5 h-3.5 text-white" /></button>
          </div>
        ) : (
          <button onClick={() => inputRef.current?.click()} className="w-full mb-3 py-8 border-2 border-dashed border-white/20 rounded-2xl hover:border-cyan-400/60 hover:bg-white/5 transition-all flex flex-col items-center gap-2 text-gray-300" data-testid="video-composer-pick-btn">
            <Video className="w-9 h-9 text-cyan-400" />
            <span className="font-semibold">{V.pickVideo}</span>
            <span className="text-xs text-gray-500">{V.maxHint}</span>
          </button>
        )}
        <input ref={inputRef} type="file" accept="video/*" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} data-testid="video-composer-input" />

        <textarea value={description} onChange={(e) => setDescription(e.target.value.split('\n').slice(0, 10).join('\n'))} placeholder={V.descPlaceholder} maxLength={2200} rows={3}
          onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250)}
          className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white placeholder-gray-500 resize-none focus:outline-none focus:border-cyan-400/60 mb-4" data-testid="video-composer-description" />

        {publishing && (
          <div className="mb-3" data-testid="video-composer-progress">
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 transition-[width]" style={{ width: `${progress}%` }} /></div>
            <p className="text-xs text-gray-400 mt-1 text-center">{progress}%</p>
          </div>
        )}

        <button onClick={publish} disabled={publishing || !file} className="w-full py-3 bg-gradient-to-r from-cyan-500 via-fuchsia-600 to-purple-700 rounded-full font-bold text-white disabled:opacity-50 hover:opacity-90 transition-opacity" data-testid="video-composer-publish">
          {publishing ? `${V.publishing} ${progress}%` : V.publish}
        </button>
      </div>
    </div>
  );
};

export default VideoComposer;
